import { formatProductArea } from '../../../../shared/utils/productFormatters.js'

import { buildCompartmentItems } from '../../utils/popoverItems.js'
import { getCompartmentName } from '../../utils/compartmentPaneItems.js'
import { COMPARTMENT_LIST_BEHAVIOR } from '../../constants/compartmentListBehavior.js'

import { DataGrid } from '../../../../shared/ui/DataGrid/index.js'

import { CompartmentTile } from './CompartmentTile.jsx'

import styles from './CompartmentsList.module.scss'

// Number of tiles per grid row — overridden per-instance via the `columns` prop.
const DEFAULT_COLUMNS = 3

/**
 * CompartmentsList — mode-aware renderer for the compartments section.
 *
 * Level 1 component — no context reads, all data via props.
 * Renders two groups of tiles (indoor / outdoor) + readonly additional areas section.
 *
 * Expanded tile details are rendered as a full-width row below the grid row
 * containing the selected tile (SELECT behavior only).
 * In ARRANGE mode: outdoor group is hidden; tiles cycle variants instead of selecting.
 *
 * @param {object[]}  props.indoorCompartments
 * @param {object[]}  props.outdoorCompartments
 * @param {string}    props.compartmentListBehavior — from COMPARTMENT_LIST_BEHAVIOR
 * @param {object}    props.compartmentTypes        — raw compartments.json
 * @param {number}   [props.columns=3]              — tiles per row (drives grid + JS row grouping)
 * @param {number|null} props.selectedCompartmentId
 * @param {number|null} props.hoveredCompartmentId
 * @param {number[]}  props.variantsCount           — index-aligned (number - 1), ARRANGE
 * @param {number[]}  props.selectedVariants        — index-aligned (number - 1), ARRANGE
 * @param {object}    props.tileSettings            — from buildCompartmentTileSettings
 * @param {object}    props.additionalAreas         — from buildAdditionalAreasSection
 * @param {Function}  props.onSelect                — (number, refTier) => void
 * @param {Function}  props.onHover                 — (number) => void
 * @param {Function}  props.onHoverLeave            — () => void
 * @param {Function}  props.onCycleVariant          — (number) => void, ARRANGE only
 * @param {Function}  props.t                       — i18n function
 */
export function CompartmentsList({
  indoorCompartments,
  outdoorCompartments,
  compartmentListBehavior,
  compartmentTypes,
  columns = DEFAULT_COLUMNS,
  selectedCompartmentId,
  hoveredCompartmentId,
  variantsCount,
  selectedVariants,
  tileSettings,
  additionalAreas,
  onSelect,
  onHover,
  onHoverLeave,
  onCycleVariant,
  t,
}) {
  const strategy = getTileStrategy(compartmentListBehavior, {
    variantsCount,
    selectedVariants,
    selectedCompartmentId,
    onSelect,
    onCycleVariant,
  })

  // ── section renderer ───────────────────────────────────────────────────────

  function renderSection(compartments, headerText) {
    if (!compartments?.length) return null

    const rows = groupIntoRows(compartments, columns)

    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>{headerText}</div>

        {rows.map((row, rowIdx) => {
          const selectedInRow = strategy.showExpanded
            ? row.find((c) => c.number === selectedCompartmentId)
            : null

          const expandedItems =
            selectedInRow && tileSettings?.tileExpansionEnabled
              ? buildCompartmentItems(selectedInRow, compartmentTypes, t, tileSettings)
              : null

          return (
            <div key={rowIdx} className={styles.rowGroup}>
              <div className={styles.tilesRow} style={{ '--cl-columns': columns }}>
                {row.map((c) => {
                  const { name, nameShort } = getCompartmentName(c.name_cad, compartmentTypes, t)
                  const isDisabled = strategy.isDisabled(c)
                  const isSelected = strategy.showExpanded && selectedCompartmentId === c.number
                  const isHovered  = hoveredCompartmentId === c.number

                  return (
                    <CompartmentTile
                      key={c.number}
                      label={nameShort}
                      title={name !== nameShort ? name : undefined}
                      value={strategy.getValue(c)}
                      isSelected={isSelected}
                      isHovered={isHovered}
                      isDisabled={isDisabled}
                      onSelect={strategy.onSelectFor(c)}
                      onHover={() => onHover(c.number)}
                      onHoverLeave={onHoverLeave}
                    />
                  )
                })}
              </div>

              {expandedItems?.length > 0 && (
                <div className={styles.expandedRow}>
                  <DataGrid items={expandedItems} size="small" columns={columns} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  // ── additional areas ───────────────────────────────────────────────────────

  const additionalItems = buildAdditionalGridItems(additionalAreas)

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.root}>
      {/* {renderSection(indoorCompartments, t('product.compartments_indoor_title'))} */}
      {renderSection(indoorCompartments)}

      {strategy.showOutdoor && renderSection(outdoorCompartments, t('product.compartments_outdoor_title'))}

      {strategy.showAdditional && additionalItems.length > 0 && (
        <div className={styles.additionalSection}>
          <DataGrid items={additionalItems} size="small" columns={1} />
        </div>
      )}
    </div>
  )
}

// ── helpers ───────────────────────────────────────────────────────────────────

/**
 * Returns a strategy object that encapsulates all mode-specific tile behavior.
 * Add a new case here when a new compartmentListBehavior is introduced.
 *
 * @returns {{ getValue(c): string, isDisabled(c): boolean, onSelectFor(c): Function|undefined,
 *             showExpanded: boolean, showAdditional: boolean, showOutdoor: boolean }}
 */
function getTileStrategy(behavior, { variantsCount, selectedVariants, onSelect, onCycleVariant }) {
  switch (behavior) {
    case COMPARTMENT_LIST_BEHAVIOR.ARRANGE: {
      return {
        getValue(c) {
          const idx        = c.number - 1
          const isMultiple = (variantsCount?.[idx] ?? 1) > 1
          return isMultiple
            ? `${(selectedVariants?.[idx] ?? 0) + 1} / ${variantsCount[idx]}`
            : '1'
        },
        isDisabled(c)  { return (variantsCount?.[c.number - 1] ?? 1) <= 1 },
        onSelectFor(c) { return this.isDisabled(c) ? undefined : () => onCycleVariant(c.number) },
        showExpanded:   false,
        showAdditional: false,
        showOutdoor:    false,
      }
    }
    default: {
      // SELECT / INFO — show area, allow selection, expand details
      return {
        getValue(c)    { return formatProductArea(c) ?? '—' },
        isDisabled()   { return false },
        onSelectFor(c) { return () => onSelect(c.number, c.ref_tier ?? null) },
        showExpanded:   true,
        showAdditional: true,
        showOutdoor:    true,
      }
    }
  }
}

function groupIntoRows(items, size) {
  const rows = []
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size))
  }
  return rows
}

/**
 * Flattens additionalAreas (from buildAdditionalAreasSection) into a DataGrid items array.
 * tooltip: prefers text_html, falls back to text; omitted when neither is set (null from builder).
 */
function buildAdditionalGridItems(additionalAreas) {
  if (!additionalAreas) return []
  const items = []

  if (additionalAreas.innerWalls) {
    const { label, value, tooltip } = additionalAreas.innerWalls
    items.push({ label, value, tooltip: tooltip?.text_html || tooltip?.text || null })
  }

  // for (const area of additionalAreas.additionalAreas ?? []) {
  //   items.push({
  //     label:   area.label,
  //     value:   area.value,
  //     tooltip: area.tooltip?.text_html || area.tooltip?.text || null,
  //   })
  // }
  for (const area of additionalAreas.additionalAreas ?? []) {
    items.push({
      label:   area.label,
      value:   area.value,
      tooltip: area.tooltip?.text_html || area.tooltip?.text || null,
    })
  }

  return items
}
